import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import OwnerPageLayout from '../../components/layout/OwnerPageLayout';
import { getApiBaseUrl } from '../../apiBase';

const REGIONS = ['CIS', 'EAST', 'EUROPE'];
const DOC_TYPES = ['PrivacyPolicy', 'TermsOfService', 'RefundPolicy', 'CookiePolicy', 'PublicOffer'];
const LANGUAGES = ['ru', 'en', 'hy', 'tr', 'de', 'fr', 'es', 'pl', 'cs', 'pt-BR', 'ar', 'az', 'ka', 'kk', 'uz'];
const EMPTY_DOC = { documentType: 'PrivacyPolicy', region: 'CIS', languageCode: 'ru', countryCode: '', title: '', content: '', version: '1.0' };

// ConsentVersion.DocumentUrl — независим от региона/языка (одно уведомление для документа
// в целом, см. ConsentController/ConsentCheckMiddleware на бэке).
const DOCUMENT_URL_BY_TYPE = {
    PrivacyPolicy: '/privacy-policy',
    TermsOfService: '/terms-of-service',
    RefundPolicy: '/refund-policy',
};

export default function LegalDocumentsAdminPage() {
    const { t } = useTranslation('legalDocs');
    const [list, setList] = useState([]);
    const [selectedId, setSelectedId] = useState(null);
    const [filterRegion, setFilterRegion] = useState('');
    const [filterType, setFilterType] = useState('');
    const [edit, setEdit] = useState({ title: '', content: '', version: '1.0', isActive: true, countryCode: '' });
    const [preview, setPreview] = useState('');
    const [creating, setCreating] = useState(false);
    const [newDoc, setNewDoc] = useState(EMPTY_DOC);
    const [createMsg, setCreateMsg] = useState('');

    const [consentVersions, setConsentVersions] = useState([]);
    const [consentForm, setConsentForm] = useState({ version: '', changeSummary: '', requiresReconsent: false, gracePeriodDays: 30 });
    const [consentBusy, setConsentBusy] = useState(false);
    const [consentMsg, setConsentMsg] = useState('');

    const token = localStorage.getItem('token');
    const api = useMemo(() => axios.create({
        baseURL: getApiBaseUrl(),
        headers: { Authorization: `Bearer ${token}` },
    }), [token]);

    useEffect(() => { load(); }, [filterRegion, filterType]);

    async function load() {
        const params = {};
        if (filterRegion) params.region = filterRegion;
        if (filterType) params.type = filterType;
        const res = await api.get('/legal/documents/admin', { params });
        setList(res.data);
    }

    async function selectDoc(id) {
        setSelectedId(id);
        setCreating(false);
        setPreview('');
        const res = await api.get(`/legal/documents/admin/${id}`);
        const d = res.data;
        setEdit({
            title: d.title || '',
            content: d.content || '',
            version: d.version || '1.0',
            isActive: d.isActive ?? true,
            countryCode: d.countryCode || '',
        });
    }

    async function save() {
        if (!selectedId) return;
        await api.put(`/legal/documents/${selectedId}`, {
            title: edit.title,
            content: edit.content,
            version: edit.version,
            isActive: edit.isActive,
            countryCode: edit.countryCode || '',
        });
        await load();
    }

    function startCreate() {
        setSelectedId(null);
        setPreview('');
        setCreateMsg('');
        setNewDoc(EMPTY_DOC);
        setCreating(true);
    }

    async function createDoc() {
        setCreateMsg('');
        if (!newDoc.title.trim() || !newDoc.content.trim()) {
            setCreateMsg(t('adminFillTitleContent', { defaultValue: 'Заполните заголовок и текст документа' }));
            return;
        }
        try {
            const res = await api.post('/legal/documents', {
                documentType: newDoc.documentType,
                region: newDoc.region,
                languageCode: newDoc.languageCode,
                countryCode: newDoc.countryCode || null,
                title: newDoc.title,
                content: newDoc.content,
                version: newDoc.version || '1.0',
                isActive: true,
            });
            await load();
            setCreating(false);
            await selectDoc(res.data.id);
        } catch (err) {
            setCreateMsg(err.response?.data?.message || t('adminCreateError', { defaultValue: 'Не удалось создать документ' }));
        }
    }

    // Пересечение — активный документ с тем же (тип, регион, язык, страна) уже есть.
    // Для newDoc проверяем вслепую по полям формы, для существующего edit — по себе же минус сам документ.
    function findOverlap(documentType, region, languageCode, countryCode, excludeId) {
        const cc = countryCode || null;
        return list.find(d =>
            d.id !== excludeId &&
            d.isActive &&
            d.documentType === documentType &&
            d.region === region &&
            d.languageCode === languageCode &&
            (d.countryCode || null) === cc
        );
    }

    async function publishVersion() {
        if (!selectedId) return;
        const res = await api.post(`/legal/documents/${selectedId}/publish`, {
            title: edit.title,
            content: edit.content,
        });
        setEdit((prev) => ({ ...prev, version: res.data.version }));
        await load();
    }

    const selectedMeta = list.find((d) => d.id === selectedId);
    const consentDocumentUrl = selectedMeta ? DOCUMENT_URL_BY_TYPE[selectedMeta.documentType] : null;

    useEffect(() => {
        if (!consentDocumentUrl) { setConsentVersions([]); return; }
        loadConsentVersions(consentDocumentUrl);
        setConsentForm({ version: '', changeSummary: '', requiresReconsent: false, gracePeriodDays: 30 });
        setConsentMsg('');
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [consentDocumentUrl]);

    async function loadConsentVersions(documentUrl) {
        const res = await api.get('/admin/consent/versions', { params: { documentUrl } });
        setConsentVersions(res.data);
    }

    async function publishConsentVersion() {
        if (!consentDocumentUrl || !consentForm.version.trim()) {
            setConsentMsg(t('consentVersionRequired', { defaultValue: 'Укажите номер версии' }));
            return;
        }
        setConsentBusy(true);
        setConsentMsg('');
        try {
            await api.post('/admin/consent/versions', {
                documentUrl: consentDocumentUrl,
                version: consentForm.version.trim(),
                changeSummary: consentForm.changeSummary.trim() || null,
                requiresReconsent: consentForm.requiresReconsent,
                gracePeriodDays: Number(consentForm.gracePeriodDays) || 0,
            });
            setConsentMsg(t('consentPublished', { defaultValue: 'Версия опубликована' }));
            setConsentForm({ version: '', changeSummary: '', requiresReconsent: false, gracePeriodDays: 30 });
            await loadConsentVersions(consentDocumentUrl);
        } catch (err) {
            setConsentMsg(err.response?.data?.message || t('consentPublishError', { defaultValue: 'Не удалось опубликовать' }));
        }
        setConsentBusy(false);
    }

    async function deactivateConsentVersion(id) {
        await api.post(`/admin/consent/versions/${id}/deactivate`);
        await loadConsentVersions(consentDocumentUrl);
    }

    async function showPreview() {
        if (!selectedId) return;
        const res = await api.post(`/legal/documents/${selectedId}/preview`);
        setPreview(res.data.html);
    }

    return (
        <OwnerPageLayout title={t('adminTitle')}>
            <div style={{ display: 'flex', gap: 12, marginBottom: 16, flexWrap: 'wrap' }}>
                <select className="sp-input" value={filterRegion} onChange={(e) => setFilterRegion(e.target.value)}>
                    <option value="">{t('adminAllRegions', { defaultValue: 'Все регионы' })}</option>
                    {REGIONS.map((r) => <option key={r} value={r}>{r}</option>)}
                </select>
                <select className="sp-input" value={filterType} onChange={(e) => setFilterType(e.target.value)}>
                    <option value="">{t('adminAllTypes', { defaultValue: 'Все типы' })}</option>
                    {DOC_TYPES.map((dt) => <option key={dt} value={dt}>{dt}</option>)}
                </select>
                <button type="button" className="sp-btn sp-btn--primary" onClick={startCreate}>
                    + {t('adminCreateNew', { defaultValue: 'Создать документ' })}
                </button>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'minmax(220px,1fr) minmax(320px,2fr)', gap: 20 }}>
                <div className="sp-panel" style={{ padding: 12, maxHeight: '70vh', overflow: 'auto' }}>
                    {list.length === 0 && (
                        <p style={{ fontSize: 13, color: '#64748b', padding: 8 }}>
                            {t('adminEmptyList', { defaultValue: 'Документов пока нет — нажмите «Создать документ» выше.' })}
                        </p>
                    )}
                    {list.map((d) => {
                        const overlap = findOverlap(d.documentType, d.region, d.languageCode, d.countryCode, d.id) && d.isActive;
                        return (
                            <button
                                key={d.id}
                                type="button"
                                className="sp-btn"
                                style={{
                                    display: 'block', width: '100%', textAlign: 'left', marginBottom: 8,
                                    background: selectedId === d.id ? 'var(--sp-primary-bg)' : '#fff',
                                    border: overlap ? '1px solid #f59e0b' : undefined,
                                    opacity: d.isActive ? 1 : 0.55,
                                }}
                                onClick={() => selectDoc(d.id)}
                                title={overlap ? t('adminOverlapHint', { defaultValue: 'Пересекается с другим активным документом того же типа/региона/языка/страны' }) : undefined}
                            >
                                <strong>{d.documentType}</strong>{!d.isActive && <span style={{ fontSize: 11, color: '#94a3b8' }}> ({t('adminInactive', { defaultValue: 'неактивен' })})</span>}
                                <div style={{ fontSize: 12, color: '#64748b' }}>
                                    {d.region}{d.countryCode ? ` · ${d.countryCode}` : ''} / {d.languageCode} v{d.version}
                                    {overlap && <span style={{ color: '#b45309', fontWeight: 700 }}> ⚠ {t('adminOverlap', { defaultValue: 'пересечение' })}</span>}
                                </div>
                            </button>
                        );
                    })}
                </div>
                <div className="sp-panel" style={{ padding: 16 }}>
                    {creating ? (
                        <>
                            <p style={{ fontSize: 13, color: '#64748b', marginBottom: 12 }}>
                                {t('adminCreateHint', { defaultValue: 'Новый документ — тип, регион и язык нельзя будет поменять после создания (создайте новый документ вместо этого).' })}
                            </p>
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: 8, marginBottom: 12 }}>
                                <select className="sp-input" value={newDoc.documentType} onChange={(e) => setNewDoc({ ...newDoc, documentType: e.target.value })}>
                                    {DOC_TYPES.map((dt) => <option key={dt} value={dt}>{dt}</option>)}
                                </select>
                                <select className="sp-input" value={newDoc.region} onChange={(e) => setNewDoc({ ...newDoc, region: e.target.value })}>
                                    {REGIONS.map((r) => <option key={r} value={r}>{r}</option>)}
                                </select>
                                <select className="sp-input" value={newDoc.languageCode} onChange={(e) => setNewDoc({ ...newDoc, languageCode: e.target.value })}>
                                    {LANGUAGES.map((l) => <option key={l} value={l}>{l}</option>)}
                                </select>
                                <input className="sp-input" placeholder={t('adminCountryPlaceholder', { defaultValue: 'Страна (RU, KZ…)' })}
                                    value={newDoc.countryCode}
                                    onChange={(e) => setNewDoc({ ...newDoc, countryCode: e.target.value.toUpperCase().slice(0, 2) })} />
                            </div>
                            {findOverlap(newDoc.documentType, newDoc.region, newDoc.languageCode, newDoc.countryCode, null) && (
                                <p style={{ fontSize: 12.5, color: '#b45309', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 8, padding: 8, marginBottom: 12 }}>
                                    ⚠ {t('adminOverlapWarning', { defaultValue: 'Уже есть активный документ с таким же типом/регионом/языком/страной — пользователи увидят только один из них (по приоритету страны, затем региона).' })}
                                </p>
                            )}
                            <input className="sp-input" style={{ marginBottom: 12 }}
                                placeholder={t('adminTitlePlaceholder', { defaultValue: 'Заголовок' })}
                                value={newDoc.title}
                                onChange={(e) => setNewDoc({ ...newDoc, title: e.target.value })} />
                            <textarea
                                className="sp-input"
                                rows={16}
                                style={{ fontFamily: 'monospace', fontSize: 12, marginBottom: 12 }}
                                placeholder={t('adminContentPlaceholder', { defaultValue: 'Текст документа (HTML)' })}
                                value={newDoc.content}
                                onChange={(e) => setNewDoc({ ...newDoc, content: e.target.value })}
                            />
                            <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 12 }}>
                                <label style={{ fontSize: 13 }}>{t('version')}:
                                    <input className="sp-input" style={{ width: 80, marginLeft: 6, display: 'inline-block' }}
                                        value={newDoc.version}
                                        onChange={(e) => setNewDoc({ ...newDoc, version: e.target.value })} />
                                </label>
                            </div>
                            {createMsg && <p style={{ fontSize: 13, color: '#b91c1c', marginBottom: 12 }}>{createMsg}</p>}
                            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                                <button type="button" className="sp-btn sp-btn--primary" onClick={createDoc}>
                                    {t('adminCreateSave', { defaultValue: 'Создать' })}
                                </button>
                                <button type="button" className="sp-btn" onClick={() => setCreating(false)}>{t('common:cancel', { defaultValue: 'Отмена' })}</button>
                            </div>
                        </>
                    ) : selectedId ? (
                        <>
                            {selectedMeta && (
                                <p style={{ fontSize: 13, color: '#64748b', marginBottom: 12 }}>
                                    {selectedMeta.region}{selectedMeta.countryCode ? ` · ${selectedMeta.countryCode}` : ''} · {selectedMeta.languageCode}
                                    {selectedMeta.updatedAt && (
                                        <> · {t('updated')}: {new Date(selectedMeta.updatedAt).toLocaleString()}</>
                                    )}
                                </p>
                            )}
                            <label style={{ fontSize: 12, color: '#64748b', display: 'block', marginBottom: 12 }}>
                                {t('adminCountryLabel', { defaultValue: 'Страна (ISO, необязательно — пусто = весь регион)' })}
                                <input className="sp-input" style={{ marginTop: 4, maxWidth: 160 }}
                                    placeholder={t('adminCountryPlaceholder', { defaultValue: 'Страна (RU, KZ…)' })}
                                    value={edit.countryCode}
                                    onChange={(e) => setEdit({ ...edit, countryCode: e.target.value.toUpperCase().slice(0, 2) })} />
                            </label>
                            {selectedMeta && findOverlap(selectedMeta.documentType, selectedMeta.region, selectedMeta.languageCode, edit.countryCode, selectedId) && edit.isActive && (
                                <p style={{ fontSize: 12.5, color: '#b45309', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 8, padding: 8, marginBottom: 12 }}>
                                    ⚠ {t('adminOverlapWarning', { defaultValue: 'Уже есть активный документ с таким же типом/регионом/языком/страной — пользователи увидят только один из них (по приоритету страны, затем региона).' })}
                                </p>
                            )}
                            <input className="sp-input" style={{ marginBottom: 12 }} value={edit.title}
                                onChange={(e) => setEdit({ ...edit, title: e.target.value })} />
                            <textarea
                                className="sp-input"
                                rows={16}
                                style={{ fontFamily: 'monospace', fontSize: 12, marginBottom: 12 }}
                                value={edit.content}
                                onChange={(e) => setEdit({ ...edit, content: e.target.value })}
                            />
                            <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 12 }}>
                                <span style={{ fontSize: 13 }}>{t('version')}: {edit.version}</span>
                            </div>
                            <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
                                <input type="checkbox" checked={edit.isActive}
                                    onChange={(e) => setEdit({ ...edit, isActive: e.target.checked })} />
                                {t('adminActive')}
                            </label>
                            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                                <button type="button" className="sp-btn sp-btn--primary" onClick={save}>{t('adminSave')}</button>
                                <button type="button" className="sp-btn" onClick={showPreview}>{t('adminPreview')}</button>
                                <button type="button" className="sp-btn" onClick={publishVersion}>
                                    {t('adminPublish', { defaultValue: 'Опубликовать новую версию' })}
                                </button>
                            </div>
                            {preview && (
                                <div className="sp-data-card" style={{ marginTop: 16, padding: 16 }}
                                    dangerouslySetInnerHTML={{ __html: preview }} />
                            )}
                        </>
                    ) : (
                        <p style={{ color: '#64748b' }}>{t('adminEdit')}</p>
                    )}
                </div>
            </div>

            {consentDocumentUrl && (
                <div className="sp-panel" style={{ padding: 16, marginTop: 20 }}>
                    <h3 style={{ marginTop: 0 }}>
                        {t('consentNotifyTitle', { defaultValue: 'Уведомление пользователям' })}
                    </h3>
                    <p style={{ fontSize: 13, color: '#64748b', marginBottom: 16, lineHeight: 1.5 }}>
                        {t('consentNotifyHint', {
                            defaultValue: 'Общее для документа {{doc}} по всем регионам и языкам (не привязано к конкретной редакции слева). По умолчанию — просто закрываемый баннер «документы обновились», никого не блокирует. Блокировку включайте только для реально существенных изменений, с переходным периодом.',
                            doc: consentDocumentUrl
                        })}
                    </p>

                    {consentVersions.find(v => v.isActive) && (
                        <div style={{
                            background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 10,
                            padding: 12, marginBottom: 16, fontSize: 13
                        }}>
                            {(() => {
                                const active = consentVersions.find(v => v.isActive);
                                return (
                                    <>
                                        <div><strong>{t('consentCurrentActive', { defaultValue: 'Текущая активная версия' })}:</strong> {active.version}</div>
                                        <div style={{ color: '#64748b', marginTop: 4 }}>
                                            {new Date(active.publishedAt).toLocaleString()}
                                            {active.changeSummary ? ` — ${active.changeSummary}` : ''}
                                        </div>
                                        <div style={{ marginTop: 4 }}>
                                            {active.requiresReconsent
                                                ? t('consentRequiresYes', {
                                                    defaultValue: 'Требует согласия — блокировка с {{date}}',
                                                    date: new Date(active.blockingFrom).toLocaleDateString()
                                                })
                                                : t('consentRequiresNo', { defaultValue: 'Только уведомление, без блокировки' })}
                                        </div>
                                    </>
                                );
                            })()}
                        </div>
                    )}

                    <div style={{ display: 'grid', gap: 10, maxWidth: 480 }}>
                        <label style={{ fontSize: 13 }}>
                            {t('consentNewVersion', { defaultValue: 'Новая версия' })}
                            <input className="sp-input" style={{ marginTop: 4 }}
                                placeholder="1.1"
                                value={consentForm.version}
                                onChange={(e) => setConsentForm({ ...consentForm, version: e.target.value })} />
                        </label>
                        <label style={{ fontSize: 13 }}>
                            {t('consentChangeSummary', { defaultValue: 'Что изменилось (покажется пользователям)' })}
                            <textarea className="sp-input" rows={3} style={{ marginTop: 4 }}
                                value={consentForm.changeSummary}
                                onChange={(e) => setConsentForm({ ...consentForm, changeSummary: e.target.value })} />
                        </label>
                        <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <input type="checkbox" checked={consentForm.requiresReconsent}
                                onChange={(e) => setConsentForm({ ...consentForm, requiresReconsent: e.target.checked })} />
                            {t('consentRequireCheckbox', { defaultValue: 'Требует повторного согласия (заблокирует доступ непринявшим)' })}
                        </label>
                        {consentForm.requiresReconsent && (
                            <label style={{ fontSize: 13 }}>
                                {t('consentGracePeriod', { defaultValue: 'Переходный период до блокировки, дней' })}
                                <input className="sp-input" type="number" min={0} style={{ marginTop: 4, maxWidth: 120 }}
                                    value={consentForm.gracePeriodDays}
                                    onChange={(e) => setConsentForm({ ...consentForm, gracePeriodDays: e.target.value })} />
                            </label>
                        )}
                        <div>
                            <button type="button" className="sp-btn sp-btn--primary" disabled={consentBusy} onClick={publishConsentVersion}>
                                {consentBusy
                                    ? t('consentPublishing', { defaultValue: 'Публикую…' })
                                    : t('consentPublishBtn', { defaultValue: 'Опубликовать и уведомить' })}
                            </button>
                        </div>
                        {consentMsg && <p style={{ fontSize: 13, color: '#334155' }}>{consentMsg}</p>}
                    </div>

                    {consentVersions.length > 0 && (
                        <div style={{ marginTop: 20 }}>
                            <h4 style={{ fontSize: 14 }}>{t('consentHistory', { defaultValue: 'История' })}</h4>
                            <div style={{ overflowX: 'auto' }}>
                                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12.5 }}>
                                    <thead>
                                        <tr style={{ borderBottom: '2px solid #e2e8f0', textAlign: 'left' }}>
                                            <th style={{ padding: '6px' }}>{t('version')}</th>
                                            <th style={{ padding: '6px' }}>{t('consentCol.published', { defaultValue: 'Опубликовано' })}</th>
                                            <th style={{ padding: '6px' }}>{t('consentCol.summary', { defaultValue: 'Что изменилось' })}</th>
                                            <th style={{ padding: '6px' }}>{t('consentCol.status', { defaultValue: 'Статус' })}</th>
                                            <th style={{ padding: '6px' }} />
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {consentVersions.map(v => (
                                            <tr key={v.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                                                <td style={{ padding: '6px', fontWeight: 600 }}>{v.version}</td>
                                                <td style={{ padding: '6px', color: '#64748b' }}>{new Date(v.publishedAt).toLocaleString()}</td>
                                                <td style={{ padding: '6px', color: '#64748b' }}>{v.changeSummary || '—'}</td>
                                                <td style={{ padding: '6px' }}>
                                                    {v.isActive ? (v.requiresReconsent ? '🔒' : '📄') : '—'}
                                                </td>
                                                <td style={{ padding: '6px' }}>
                                                    {v.isActive && (
                                                        <button type="button" className="sp-btn" style={{ padding: '4px 8px', fontSize: 12 }}
                                                            onClick={() => deactivateConsentVersion(v.id)}>
                                                            {t('consentDeactivate', { defaultValue: 'Отключить' })}
                                                        </button>
                                                    )}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}
                </div>
            )}
        </OwnerPageLayout>
    );
}
